import { HttpClient, HttpContext, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import {
  GiftCardPortalConnectModel,
  GiftCardPortalModel,
  PortalShowCancelRequest,
  PortalShowModel,
  PortalShowsParams,
  SubscriptionInvoicePayRequestModel,
  SubscriptionInvoicePayResultModel,
  SubscriptionMandateSetupResultModel,
  SubscriptionPortalCancelModel,
  SubscriptionPortalCancelResultModel,
  SubscriptionPortalCardModel,
  SubscriptionPortalChangeBillingPeriodModel,
  SubscriptionPortalConnectModel,
  SubscriptionPortalDetailModel,
  SubscriptionPortalInvoiceModel,
  SubscriptionPortalItemModel,
  SubscriptionPortalNoShowReasonModel,
  SubscriptionPortalShowModel,
  SubscriptionPortalUpdateUserDetailsModel,
  SubscriptionPortalUserDetailsModel,
} from '@digilize/shared/definitions/src/lib/models';
import { DataTablesList } from '@digilize/shared/definitions/src';

@Injectable({
  providedIn: 'root',
})
export class SubscriptionPortalApiService {
  private readonly BASE_URL = '/api/v1.0/portal/subscriptions';

  // The list and the detail are the only two routes that have a v2; everything
  // below is unchanged and still v1. v2 rows are complete — passes, billing,
  // can_edit_details and the attention list all inline — so one call draws the
  // whole overview where v1 cost 1 + 5N (PAUL-3288).
  private readonly BASE_URL_V2 = '/api/v2.0/portal/subscriptions';

  constructor(private http: HttpClient) {}

  getSubscriptions = () => this.http.get<SubscriptionPortalItemModel[]>(this.BASE_URL_V2);

  getSubscription = (id: string) => this.http.get<SubscriptionPortalItemModel>(`${this.BASE_URL_V2}/${id}`);

  getCards = (id: string) => this.http.get<SubscriptionPortalCardModel[]>(`${this.BASE_URL}/${id}/cards`);

  // The scannable image of a card lives outside the portal namespace:
  // SubscriptionInstancesApiService.getCardQrPng (/subscription-instances/cards/{cardId}/qr.png),
  // shared with the dashboard's print flow.

  // Buyer-only — recurring subscriptions only.
  setBillingPeriod = (id: string, body: SubscriptionPortalChangeBillingPeriodModel) =>
    this.http.put<void>(`${this.BASE_URL}/${id}/billing-period`, body);

  // Readable by any linked user (holder, buyer or cardholder). Carries the stored
  // values, the product's per-field requirements, the still-missing field names
  // and `can_edit` — the flag the edit affordance is gated on.
  getUserDetails = (id: string) =>
    this.http.get<SubscriptionPortalUserDetailsModel>(`${this.BASE_URL}/${id}/user-details`);

  // Full replace: omitted fields are cleared, so always send back the whole block
  // read from getUserDetails. Reactivates the instance if it was paused for
  // missing details. On a recurring subscription only the buyer or the linked
  // holder may write (403 `subscription_portal_not_buyer`); non-recurring allows
  // any linked user.
  setUserDetails = (id: string, body: SubscriptionPortalUpdateUserDetailsModel) =>
    this.http.put<void>(`${this.BASE_URL}/${id}/user-details`, body);

  cancel = (id: string, body: SubscriptionPortalCancelModel = {}) =>
    this.http.post<SubscriptionPortalCancelResultModel>(`${this.BASE_URL}/${id}/cancel`, body);

  getShows = (id: string) => this.http.get<SubscriptionPortalShowModel[]>(`${this.BASE_URL}/${id}/shows`);

  // Stores the user-provided no-show reason for a past show that was not visited
  // (only when a no-show policy is set on the subscription).
  // `context` as on cancelPortalShow: the tickets pop-up answers a failed save on the box the
  // reason was typed into, so it passes SKIP_ERROR_INTERCEPTOR rather than let a snackbar say it
  // twice. Omitting it keeps the interceptor's default.
  setNoShowReason = (id: string, showId: string, body: SubscriptionPortalNoShowReasonModel, context?: HttpContext) =>
    this.http.post<void>(`${this.BASE_URL}/${id}/shows/${showId}/no-show-reason`, body, {
      ...(context ? { context } : {}),
    });

  // Every show the signed-in customer has, whatever booked it — subscription
  // bookings, tickets, Cineville and held carts in one paged list, one row per
  // SHOW with its tickets in `admissions`. Replaces the per-instance getShows
  // fan-out for the portal's shows tab.
  getPortalShows = (params: PortalShowsParams = {}) => {
    const { search, ...rest } = params;
    let httpParams = new HttpParams();
    Object.entries(rest).forEach(([key, value]) => {
      if (value != null) {
        httpParams = httpParams.set(key, String(value));
      }
    });
    // Must be the dot form: `search[value]` does not bind server-side and
    // silently returns everything unfiltered.
    if (search) {
      httpParams = httpParams.set('search.value', search);
    }
    return this.http.get<DataTablesList<PortalShowModel>>('/api/v1.0/portal/shows', { params: httpParams });
  };

  // Cancels one admission, whatever booked it — post back the `source` and `id`
  // off the admission unchanged and the endpoint resolves the route. Not
  // idempotent: cancelling something already cancelled is a 400, since
  // `can_cancel` is false by then. Cancelling a `reservation` releases its WHOLE
  // hold, so always re-fetch the list afterwards rather than dropping the one
  // entry client-side.
  // `context` is for callers that answer the 400s themselves: the tickets pop-up passes
  // SKIP_ERROR_INTERCEPTOR, since a refusal belongs on the ticket's own row and a stale
  // `can_cancel` is nothing to pop a snackbar over. Omitting it keeps the interceptor's default.
  cancelPortalShow = (body: PortalShowCancelRequest, context?: HttpContext) =>
    this.http.post<void>('/api/v1.0/portal/shows/cancel', body, { ...(context ? { context } : {}) });

  // Still v1, and still the v1 flat projection — the portal reloads its overview
  // after connecting rather than rendering from this response.
  connect = (body: SubscriptionPortalConnectModel) =>
    this.http.post<SubscriptionPortalDetailModel>(`${this.BASE_URL}/connect`, body);

  // The caller leaves a subscription somebody else pays for: every seat they hold
  // is freed, so the row goes from their list and {id}/shows stops answering for
  // them. 200 with an empty body — reload the list afterwards. Refused with
  // `subscription_connection_is_buyer` for the buyer, who cancels instead.
  disconnect = (id: string) => this.http.delete<void>(`${this.BASE_URL}/${id}/connection`);

  // The buyer takes one account's access back. `userId` is `connections[].user_id`
  // off the buyer's own row, never the subscription id. Still a v1 route, but it
  // answers with the v2-shaped row (`SubscriptionPortalItemModel`) refreshed, so
  // the connections list re-renders without a second request.
  retractConnection = (id: string, userId: string) =>
    this.http.delete<SubscriptionPortalItemModel>(`${this.BASE_URL}/${id}/connections/${userId}`);

  getInvoices = (id: string) => this.http.get<SubscriptionPortalInvoiceModel[]>(`${this.BASE_URL}/${id}/invoices`);

  // TODO(BE): endpoint does not exist yet. PDF of one invoice ("each invoice can
  // be downloaded"). Same blob pattern as the card QR.
  downloadInvoice = (id: string, invoiceId: string) =>
    this.http.get(`${this.BASE_URL}/${id}/invoices/${invoiceId}/download`, { responseType: 'blob' });

  payInvoice = (id: string, invoiceId: string, body: SubscriptionInvoicePayRequestModel = {}) =>
    this.http.post<SubscriptionInvoicePayResultModel>(`${this.BASE_URL}/${id}/invoices/${invoiceId}/pay`, body);

  // Buyer-only — re-runs the iDEAL-first SEPA mandate setup on a new bank account.
  // Replaces the buyer's mandate for ALL their recurring subscriptions at the company.
  changePaymentMethod = (id: string, body: SubscriptionInvoicePayRequestModel = {}) =>
    this.http.post<SubscriptionMandateSetupResultModel>(`${this.BASE_URL}/${id}/change-payment-method`, body);

  getGiftCards = () => this.http.get<GiftCardPortalModel[]>('/api/v1.0/portal/gift-cards');

  // Attaches a card the account never bought online — sold at a register, loaded as a refund,
  // received as a gift. An expired or empty card connects too and comes back labelled; a card
  // sitting on another account is taken over. Failed attempts share the rate-limit budget with
  // `connect` above, so a 429 here can be spent by either.
  connectGiftCard = (body: GiftCardPortalConnectModel) =>
    this.http.post<GiftCardPortalModel>('/api/v1.0/portal/gift-cards/connect', body);
}
